import type { ReactNode } from 'react'

// Section shell used by every review section (spec §8.2): header with title,
// optional "💬 N" comment-count badge, optional "+ Comment" action, and an
// anchorRef for the gutter-comment placement engine (§8.1) to measure.

type SectionCardProps = {
  title: ReactNode
  onAddComment?: () => void
  anchorRef?: React.Ref<HTMLDivElement>
  children: ReactNode
} & (
  | {
      /**
       * Whether the section has anything of its own to show. A section that
       * can legitimately be empty — no complexity claim, no verification
       * counts, no post-mortem — passes `false` and disappears, rather than
       * rendering a card that asserts nothing (LG-821).
       *
       * The card, not the caller, ANDs that with `commentCount`, because the
       * two conditions are one invariant and it used to be four hand-copied
       * guards: a review already carrying comments on the section still
       * mounts it, or those comments are invisible to the coach while the tab
       * counter keeps counting them — the LG-632 failure.
       *
       * `commentCount` is required alongside it rather than optional, which
       * is what makes that structural instead of documented: the override is
       * not expressible without the number that performs it, so opting into
       * hiding forces you to declare what overrides the hiding. Sections that
       * always mount omit both.
       *
       * Note this governs mounting only. A section can still unmount for
       * reasons the card cannot see — the parent stops rendering it, the
       * submission switches — so nothing anchored to a section may assume its
       * anchor outlives it. See LG-828 for the open composer, which does.
       */
      hasContent: boolean
      commentCount: number
    }
  | { hasContent?: undefined; commentCount?: number }
)

export function SectionCard(props: SectionCardProps): ReactNode {
  const {
    title,
    commentCount,
    hasContent = true,
    onAddComment,
    anchorRef,
    children,
  } = props

  const hasComments = (commentCount ?? 0) >= 1

  if (!hasContent && !hasComments) return null

  return (
    <div
      ref={anchorRef}
      className="rounded-xl border border-(--cd-border) bg-(--cd-card) p-4"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-(--cd-text)">{title}</h3>
          {hasComments && (
            <span className="rounded-full bg-(--cd-inset) px-2 py-0.5 text-xs text-(--cd-text-muted)">
              💬 {commentCount}
            </span>
          )}
        </div>
        {onAddComment && (
          <button
            type="button"
            onClick={onAddComment}
            className="text-xs font-medium text-(--cd-text-muted) hover:text-(--cd-text)"
          >
            + Comment
          </button>
        )}
      </div>
      {children}
    </div>
  )
}
